<template>
	<div class="modal-overlay">
		<div class="modal-content">
			<div class="modal-header">
				<h3>Decision Error Processing</h3>
				<span class="close" @click="handleClose" :class="{ disabled: isSubmitting }">&times;</span>
			</div>

			<div class="modal-body">
				<p>Before performing a re-decision, please confirm the following:</p>
				<form>
					<div v-if="isLoading" class="spinner-container">
						<Spinner />
					</div>
					<div v-else class="form-container">
						<div class="form-input">
							<label for="issueResolved" class="checkbox-label">
								<input
									type="checkbox"
									id="issueResolved"
									name="issueResolved"
									v-model="issueResolved"
									:disabled="isInputDisabled"
									required />
								<span class="custom-checkbox"></span>
								<span>
									The issue causing the decision error has been identified and resolved.
									<span class="required">*</span>
								</span>
							</label>
						</div>
						<div class="form-input">
							<label>
								Do you want to re-decision only (use existing AR credit reports) or re-pull credit &
								re-decision?
								<span class="required">*</span>
							</label>
							<div class="radio-group">
								<label class="radio-label">
									<input
										type="radio"
										name="redecisionOption"
										value="redecisionOnly"
										v-model="redecisionOption"
										:disabled="isInputDisabled"
										required />
									<span class="custom-radio"></span>
									Re-decision only
								</label>
								<label class="radio-label">
									<input
										type="radio"
										name="redecisionOption"
										value="repullAndRedecision"
										v-model="redecisionOption"
										:disabled="isInputDisabled"
										required />
									<span class="custom-radio"></span>
									Re-pull credit & Re-decision
								</label>
							</div>
						</div>
						<div v-if="isBorrowerSelectionRequired" class="form-input select">
							<label class="required">
								For which borrower(s) do you want to perform the soft credit re-pull?
								<span class="required">*</span>
							</label>
							<select
								v-model="selectedBorrowers"
								:disabled="!isBorrowerSelectionRequired || isInputDisabled"
								required>
								<option key="none" value="" disabled selected>Select an option</option>
								<option v-for="option in borrowerOptions" :key="option.key" :value="option.value">
									{{ option.label }}
								</option>
							</select>
						</div>
					</div>
				</form>
				<p>
					By clicking the <i>“Submit”</i> button, I am kicking off the process to re-decision this line of
					credit for annual review.
				</p>
				<div v-if="errorProcessing" class="error-container">
					<ExclamationCircleIcon class="error-icon" />
					<p class="error">The re-decision failed to process. Please close this and try again later.</p>
				</div>
			</div>

			<div class="modal-footer">
				<Spinner v-if="isSubmitting" class="small" />
				<button class="secondary" @click="handleClose" :disabled="isSubmitting">Cancel</button>
				<button @click="submitRedecisionAsync" :disabled="!isFormValid || isSubmitting">Submit</button>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { onMounted, onUnmounted, ref, computed } from 'vue';
	import axios from 'axios';
	import functions from '@/use/functions';
	import Spinner from '@/components/shared/Spinner.vue';
	import { LoanBorrower } from '@/models/loans';
	import { ExclamationCircleIcon } from '@heroicons/vue/24/solid';

	const props = defineProps<{
		annualReviewId?: number;
		annualReviewYear?: number;
		loanId: number;
		onClose: () => void;
	}>();

	const isLoading = ref<boolean>(false);
	const isSubmitting = ref<boolean>(false);
	const errorProcessing = ref<boolean>(false);

	const issueResolved = ref<boolean>(false);
	const redecisionOption = ref<string | null>(null);
	const selectedBorrowers = ref<number | null>(null);

	const borrowers = ref<LoanBorrower[]>([]);
	const primaryBorrower = ref<LoanBorrower>();
	const coBorrower = ref<LoanBorrower | null>(null);

	const isBorrowerSelectionRequired = computed(() => redecisionOption.value === 'repullAndRedecision');

	const isFormValid = computed(() => {
		const hasIssueResolved = issueResolved.value;
		const hasRedecisionOption = redecisionOption.value !== null;

		const hasSelectedBorrower =
			redecisionOption.value === 'repullAndRedecision' ? selectedBorrowers.value !== null : true;

		return hasIssueResolved && hasRedecisionOption && hasSelectedBorrower;
	});

	const isInputDisabled = computed(() => isLoading.value || isSubmitting.value);

	const borrowerOptions = computed(() => {
		if (!coBorrower.value) {
			return [{ key: 'primary', value: 0, label: primaryBorrower.value?.fullName }];
		}

		return [
			{ key: 'primary', value: 0, label: primaryBorrower.value?.fullName },
			{ key: 'co', value: 1, label: coBorrower.value?.fullName },
			{ key: 'both', value: 2, label: 'Both' }
		];
	});

	function handleClose() {
		if (isSubmitting.value) {
			return;
		}

		props.onClose();
	}

	async function submitRedecisionAsync() {
		isSubmitting.value = true;
		errorProcessing.value = false;

		try {
			const queryParams = {
				loanId: props.loanId
			};

			let request: { borrowerId: number; firstName: string; lastName: string }[];

			if (redecisionOption.value === 'repullAndRedecision') {
				const selectedBorrowersList = borrowers.value.filter((b) => {
					switch (selectedBorrowers.value) {
						case 0:
							return b.borrowerTypeId === 1;
						case 1:
							return b.borrowerTypeId === 2;
						case 2:
							return true;
						default:
							return false;
					}
				});

				request = selectedBorrowersList.map((b) => ({
					borrowerId: b.id,
					firstName: b.firstName,
					lastName: b.lastName
				}));
			} else {
				request = [];
			}

			console.log(request);

			await axios.post(`/api/annualreview/${props.annualReviewId}/redecision`, request, { params: queryParams });

			functions.openModal({
				title: 'Success!',
				description: 'Re-decision successfully queued for annual review.'
			});

			props.onClose();
		} catch {
			errorProcessing.value = true;
		} finally {
			isSubmitting.value = false;
		}
	}

	async function fetchRepullDetailsAsync() {
		isLoading.value = true;

		try {
			const response = await axios.get<LoanBorrower[]>(`/api/loanservice/loans/${props.loanId}/borrowers`);

			borrowers.value = response.data.map(
				(b) => new LoanBorrower(b.id, b.borrowerTypeId, b.firstName, b.lastName)
			);

			primaryBorrower.value = borrowers.value.find((b) => b.borrowerTypeId === 1);
			coBorrower.value = borrowers.value.find((b) => b.borrowerTypeId === 2) || null;
		} catch {
			functions.openModal({
				title: 'Oops!',
				description: `There was an issue getting the re-decision details. Please try again later.`
			});
		} finally {
			isLoading.value = false;
		}
	}

	onMounted(async () => {
		document.body.style.overflow = 'hidden';

		await fetchRepullDetailsAsync();
	});

	onUnmounted(() => {
		document.body.style.overflow = '';
	});
</script>

<style scoped src="@/assets/css/loans/form.css"></style>
<style scoped src="@/assets/css/loans/repull-modal.css"></style>